import type { LocationsSectionContent, Location } from "@site-factory/config";
import { cn } from "../utils";
import { formatDayHours, getLocationPhone } from "../utils/location";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { Card, CardContent } from "../components/Card";
import { SectionShell } from "../components/SectionShell";
import { Badge } from "../components/Badge";
import { Tooltip } from "../components/Tooltip";
import { Skeleton, SkeletonAvatar, SkeletonText } from "../components/Skeleton";
import { OptimizedImage } from "../components/OptimizedImage";
import { MapEmbed } from "../components/MapEmbed";
import { Icon } from "../icons";
import { useState } from "react";

export interface LocationsSectionProps extends LocationsSectionContent {
  className?: string;
  loading?: boolean;
}

function LocationCard({ location }: { location: Location }) {
  return (
    <Card id={location.slug || location.id} className="h-full shadow-sm-2 hover:shadow-md-2 transition">
      <CardContent className="p-6 space-y-4">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-xl font-semibold">{location.name}</h3>
            {location.isPrimary && (
              <Badge variant="accent" className="shrink-0">
                Primary
              </Badge>
            )}
          </div>
          {location.description && (
            <p className="text-sm" style={{ color: "var(--color-text,#0f172a)", opacity: 0.78 }}>
              {location.description}
            </p>
          )}
        </div>

        {location.image && (
          <div className="aspect-video rounded-lg overflow-hidden bg-neutral-100">
            <OptimizedImage
              src={location.image}
              alt={location.name}
              aspectRatio="16/9"
              placeholder="blur"
              blurDataURL={location.image}
              className="h-full w-full"
            />
          </div>
        )}

        <div className="space-y-1">
          <h4
            className="text-sm font-semibold uppercase tracking-wide"
            style={{ color: "var(--color-text,#0f172a)", opacity: 0.82 }}
          >
            Address
          </h4>
          <address className="not-italic text-sm" style={{ color: "var(--color-text,#0f172a)", opacity: 0.8 }}>
            {location.address.street1}
            {location.address.street2 && (
              <>
                <br />
                {location.address.street2}
              </>
            )}
            <br />
            {location.address.city}, {location.address.state} {location.address.zip}
            {location.address.country && (
              <>
                <br />
                {location.address.country}
              </>
            )}
          </address>
        </div>

        {location.hours && location.hours.length > 0 && (
          <div className="space-y-1">
            <h4
              className="text-sm font-semibold uppercase tracking-wide"
              style={{ color: "var(--color-text,#0f172a)", opacity: 0.82 }}
            >
              Hours
            </h4>
            <div className="text-sm space-y-1">
              {location.hours.map((dayHours) => (
                <div key={dayHours.day} className="flex justify-between items-center py-0.5">
                  <span style={{ color: "var(--color-text,#0f172a)", opacity: 0.78 }}>{dayHours.day}</span>
                  <span
                    className={cn("font-medium", dayHours.closed && "opacity-60")}
                    style={{ color: "var(--color-text,#0f172a)" }}
                  >
                    {formatDayHours(dayHours)}
                  </span>
                </div>
              ))}
            </div>
            {location.hoursNote && (
              <p className="text-xs italic mt-2" style={{ color: "var(--color-text,#0f172a)", opacity: 0.7 }}>
                {location.hoursNote}
              </p>
            )}
            {location.seasonalNotes && (
              <p className="mt-2 rounded-lg bg-accent/10 px-3 py-2 text-xs font-medium text-neutral-700">
                {location.seasonalNotes}
              </p>
            )}
          </div>
        )}

        {location.menuHighlights && location.menuHighlights.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-neutral-200">
            <h4
              className="text-sm font-semibold uppercase tracking-wide"
              style={{ color: "var(--color-text,#0f172a)", opacity: 0.82 }}
            >
              Menu highlights
            </h4>
            <ul className="text-sm space-y-1" style={{ color: "var(--color-text,#0f172a)", opacity: 0.8 }}>
              {location.menuHighlights.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span aria-hidden="true">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {location.contact && (
          <div className="space-y-2 pt-2 border-t border-neutral-200">
            {getLocationPhone(location) && (
              <a href={`tel:${getLocationPhone(location)}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                <Icon name="phone" className="h-4 w-4" />
                <span>{getLocationPhone(location)}</span>
              </a>
            )}
            {location.contact.email && (
              <a
                href={`mailto:${location.contact.email}`}
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Icon name="mail" className="h-4 w-4" />
                <span>{location.contact.email}</span>
              </a>
            )}
            {location.contact.website && (
              <a
                href={location.contact.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Icon name="globe" className="h-4 w-4" />
                <span>Visit Website</span>
              </a>
            )}
            {location.contact.social && (
              <div className="flex gap-3 pt-1">
                {location.contact.social.facebook && (
                  <Tooltip label="Facebook">
                    <a
                      href={location.contact.social.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg hover:opacity-70 transition"
                      aria-label="Facebook"
                    >
                      <Icon name="facebook" className="h-4 w-4" />
                    </a>
                  </Tooltip>
                )}
                {location.contact.social.instagram && (
                  <Tooltip label="Instagram">
                    <a
                      href={location.contact.social.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg hover:opacity-70 transition"
                      aria-label="Instagram"
                    >
                      <Icon name="instagram" className="h-4 w-4" />
                    </a>
                  </Tooltip>
                )}
                {location.contact.social.twitter && (
                  <Tooltip label="Twitter">
                    <a
                      href={location.contact.social.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg hover:opacity-70 transition"
                      aria-label="Twitter"
                    >
                      <Icon name="twitter" className="h-4 w-4" />
                    </a>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        )}

        {location.amenities && location.amenities.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-neutral-200">
            <h4
              className="text-sm font-semibold uppercase tracking-wide"
              style={{ color: "var(--color-text,#0f172a)", opacity: 0.82 }}
            >
              Amenities
            </h4>
            <div className="flex flex-wrap gap-2">
              {location.amenities.map((amenity, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {amenity}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function LocationsSection({
  title,
  subtitle,
  locations,
  variant = "cards",
  showMap,
  className,
  loading = false,
}: LocationsSectionProps) {
  const [selectedId, setSelectedId] = useState<string | null>(locations[0]?.id ?? null);
  const sortedLocations = [...locations].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return a.name.localeCompare(b.name);
  });
  const selected = sortedLocations.find((loc) => loc.id === selectedId) ?? sortedLocations[0];
  const hasCoords = sortedLocations.some((loc) => loc.coordinates);
  const mapEnabled = !loading && hasCoords && (variant === "map" || showMap !== false);

  const skeletons = Array.from({ length: 3 });

  const SkeletonCard = () => (
    <Card className="h-full bg-white/90 shadow-sm-2">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-6 w-16" />
        </div>
        <Skeleton className="h-3 w-64" />
        <Skeleton className="h-40 w-full rounded-lg" />
        <SkeletonText lines={3} />
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <SkeletonText lines={3} />
        </div>
        <div className="flex items-center gap-3">
          <SkeletonAvatar size={20} />
          <Skeleton className="h-3 w-28" />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <SectionShell className={className} backgroundClassName="bg-surface">
      {(title || subtitle) && (
        <div className="text-center mb-12">
          {title && <SectionTitle>{title}</SectionTitle>}
          {subtitle && <SectionSubtitle className="mt-4">{subtitle}</SectionSubtitle>}
        </div>
      )}

      {loading ? (
        <div className={variant === "cards" ? "grid gap-8 md:grid-cols-2 lg:grid-cols-3" : "space-y-6"}>
          {skeletons.map((_, idx) => (
            <SkeletonCard key={idx} />
          ))}
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-start">
          <div className="grid gap-8 md:grid-cols-2">
            {variant === "cards" || variant === "map"
              ? sortedLocations.map((location) => <LocationCard key={location.id} location={location} />)
              : sortedLocations.map((location) => (
                  <Card key={location.id} className="p-0">
                    <CardContent className="p-0">
                      <LocationCard location={location} />
                    </CardContent>
                  </Card>
                ))}
          </div>
          {mapEnabled ? (
            <div className="sticky top-24 space-y-3">
              <h3
                className="text-sm font-semibold uppercase tracking-wide"
                style={{ color: "var(--color-text,#0f172a)", opacity: 0.82 }}
              >
                View on map
              </h3>
              <div className="flex gap-2 flex-wrap">
                {sortedLocations.map((loc) => (
                  <button
                    key={loc.id}
                    onClick={() => setSelectedId(loc.id)}
                    className={cn(
                      "px-3 py-1 rounded-full text-sm border transition",
                      selected?.id === loc.id
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-[color:var(--color-text,#0f172a)]/20 text-[color:var(--color-text,#0f172a)]/80"
                    )}
                    aria-label={`Show ${loc.name} in ${loc.address.city}, ${loc.address.state} on map`}
                  >
                    {loc.name} • {loc.address.city}
                  </button>
                ))}
              </div>
              {selected?.coordinates ? (
                <MapEmbed
                  lat={selected.coordinates.lat}
                  lng={selected.coordinates.lng}
                  label={selected.name}
                  title={`${selected.name} - ${selected.address.city}, ${selected.address.state}`}
                />
              ) : (
                <div className="text-sm" style={{ color: "var(--color-text,#0f172a)", opacity: 0.78 }}>
                  {selected ? `No coordinates provided for ${selected.name}.` : "Select a location to view it on the map."}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </SectionShell>
  );
}
